/** Generic √-scaled funnel sankey.
 *
 * Stage volumes are labelled with their actual counts while band widths are
 * √-scaled, so small stages stay legible next to a large cohort base (a
 * value-strict sankey collapses them to slivers). The caller supplies nodes
 * (positioned by integer `col`) and links; this component handles layout,
 * the cubic-bezier ribbons, and theming. Shared by the KPI section's sales /
 * marketing / partners funnels — each builds its own topology.
 *
 * Tones map to the app's theme tokens so the chart tracks light/dark:
 *   brand   — progressing / won path        (Cinch cyan)
 *   danger  — lost / churned / de-booked     (red)
 *   warning — pending: still open / not booked (amber)
 *   neutral — uncategorised: other / M&A / reseller (muted)
 */

export type FunnelTone = "brand" | "danger" | "warning" | "neutral";

export interface FunnelNode {
  id: string;
  label: string;
  sublabel?: string;
  value: number;
  /** 0-based column index, left → right. */
  col: number;
  tone: FunnelTone;
}

export interface FunnelLink {
  from: string;
  to: string;
  value: number;
  tone: FunnelTone;
}

const TONE_COLOR: Record<FunnelTone, string> = {
  brand: "hsl(var(--cinch-brand))",
  danger: "hsl(var(--status-danger))",
  warning: "hsl(var(--status-warning))",
  neutral: "hsl(var(--muted-foreground))",
};

const BAND_OPACITY: Record<FunnelTone, number> = {
  brand: 0.17,
  danger: 0.16,
  warning: 0.24,
  neutral: 0.22,
};

const NODE_W = 132;
const COL_GAP = 16; // vertical gap between stacked nodes in a column
const TOP_PAD = 26; // room for the stage labels
const BOT_PAD = 14;

export function FunnelSankey({
  nodes,
  links,
  stages,
  width = 1040,
  ariaLabel,
  onNodeClick,
}: {
  nodes: FunnelNode[];
  links: FunnelLink[];
  stages?: string[];
  width?: number;
  ariaLabel: string;
  /** When set, each node becomes a button that reports its id on click. */
  onNodeClick?: (id: string) => void;
}) {
  if (nodes.length === 0) return null;

  const cols = Math.max(...nodes.map((n) => n.col)) + 1;
  const colStep = cols > 1 ? (width - NODE_W) / (cols - 1) : 0;
  const maxV = Math.max(1, ...nodes.map((n) => n.value));

  // Node band heights: min 34px so the count + label always fit; √-scaled
  // above that. Link bands use the same √-curve, capped to their endpoints.
  const scale = (v: number) => (v <= 0 ? 34 : 38 + 70 * Math.sqrt(v / maxV));
  const bandScale = (v: number) => (v <= 0 ? 2 : 7 + 58 * Math.sqrt(v / maxV));

  // Lay out each column as a centred vertical stack; the tallest column sets
  // the content height and shorter columns centre within it.
  const byCol: FunnelNode[][] = Array.from({ length: cols }, () => []);
  for (const n of nodes) byCol[n.col]?.push(n);
  const colHeight = (col: FunnelNode[]) =>
    col.reduce((s, n) => s + scale(n.value), 0) + COL_GAP * Math.max(0, col.length - 1);
  const contentH = Math.max(1, ...byCol.map(colHeight));
  const H = TOP_PAD + contentH + BOT_PAD;

  const pos = new Map<string, { x: number; y: number; h: number }>();
  byCol.forEach((col, ci) => {
    let y = TOP_PAD + (contentH - colHeight(col)) / 2;
    for (const n of col) {
      const h = scale(n.value);
      pos.set(n.id, { x: ci * colStep, y, h });
      y += h + COL_GAP;
    }
  });

  // Spread multiple links across a node's edge so parallel ribbons don't
  // stack on one point.
  const outBy = new Map<string, FunnelLink[]>();
  const inBy = new Map<string, FunnelLink[]>();
  for (const l of links) {
    outBy.set(l.from, [...(outBy.get(l.from) ?? []), l]);
    inBy.set(l.to, [...(inBy.get(l.to) ?? []), l]);
  }

  const bands = links.map((l) => {
    const s = pos.get(l.from);
    const t = pos.get(l.to);
    if (!s || !t) return null;
    const th = Math.min(bandScale(l.value), s.h * 0.92, t.h * 0.92);
    const sib = outBy.get(l.from) ?? [];
    const tsib = inBy.get(l.to) ?? [];
    const y1 = s.y + s.h * ((sib.indexOf(l) + 1) / (sib.length + 1));
    const y2 = t.y + t.h * ((tsib.indexOf(l) + 1) / (tsib.length + 1));
    const x1 = s.x + NODE_W;
    const x2 = t.x;
    const cx = (x1 + x2) / 2;
    const d = [
      `M ${x1} ${y1 - th / 2}`,
      `C ${cx} ${y1 - th / 2}, ${cx} ${y2 - th / 2}, ${x2} ${y2 - th / 2}`,
      `L ${x2} ${y2 + th / 2}`,
      `C ${cx} ${y2 + th / 2}, ${cx} ${y1 + th / 2}, ${x1} ${y1 + th / 2}`,
      "Z",
    ].join(" ");
    return { key: `${l.from}-${l.to}`, d, color: TONE_COLOR[l.tone], opacity: BAND_OPACITY[l.tone] };
  });

  return (
    <svg viewBox={`0 0 ${width} ${H}`} className="h-auto w-full" role="img" aria-label={ariaLabel}>
      {stages?.map((s, i) => (
        <text
          key={`stage-${i}`}
          x={i * colStep + NODE_W / 2}
          y={13}
          textAnchor="middle"
          className="fill-muted-foreground"
          fontSize={9.5}
          fontWeight={700}
          letterSpacing={0.6}
        >
          {s.toUpperCase()}
        </text>
      ))}
      {bands.map(
        (b) => b && <path key={b.key} d={b.d} fill={b.color} opacity={b.opacity} />,
      )}
      {nodes.map((n) => {
        const p = pos.get(n.id);
        if (!p) return null;
        const color = TONE_COLOR[n.tone];
        const cy = p.y + p.h / 2;
        const withSub = !!n.sublabel && p.h >= 58;
        const clickable = !!onNodeClick;
        return (
          <g
            key={n.id}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            aria-label={clickable ? `${n.label}: ${n.value} — view deals` : undefined}
            className={clickable ? "group cursor-pointer focus:outline-none" : undefined}
            onClick={clickable ? () => onNodeClick(n.id) : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onNodeClick(n.id);
                    }
                  }
                : undefined
            }
          >
            <rect x={p.x} y={p.y} width={NODE_W} height={p.h} rx={8} fill={color} opacity={0.1} />
            <rect x={p.x} y={p.y} width={3.5} height={p.h} rx={1.75} fill={color} opacity={0.85} />
            <text
              x={p.x + NODE_W / 2}
              y={withSub ? cy - 7 : cy - 3}
              textAnchor="middle"
              className="fill-foreground"
              fontSize={16}
              fontWeight={800}
            >
              {n.value.toLocaleString("en-US")}
            </text>
            <text
              x={p.x + NODE_W / 2}
              y={withSub ? cy + 8 : cy + 12}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize={9}
              fontWeight={700}
              letterSpacing={0.5}
            >
              {n.label.toUpperCase()}
            </text>
            {withSub && (
              <text
                x={p.x + NODE_W / 2}
                y={cy + 19}
                textAnchor="middle"
                className="fill-muted-foreground"
                fontSize={8.5}
              >
                {n.sublabel}
              </text>
            )}
            {clickable && (
              <rect
                x={p.x}
                y={p.y}
                width={NODE_W}
                height={p.h}
                rx={8}
                fill="transparent"
                stroke={color}
                strokeWidth={2}
                className="pointer-events-none opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}
